import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import deepEqual from 'deep-equal';
import type { Content, ContentType } from '@ilteducation/content-registry-types';
import { Filter, GroupFilterMode, SearchQuery, Sort } from './types';
import { searchContent } from './api';

type SearchState<C extends Content> = {
  contentType: string;
  query?: string;
  filter?: Filter;
  sort?: Sort[];
  cursor: string | null;
  pageSize?: number;
  pageTotal: number;
  isLoading: boolean;
  contents: C[];
  groupFilterMode: GroupFilterMode;
  randomize: boolean;
};

const getRandomSeed = (): number => {
  return Math.floor(Math.random() * 1000000);
};

const useSearch = <C extends Content>(contentType: ContentType) =>
  defineStore(`content-search-${contentType}`, () => {
    const searchState = ref<SearchState<C>>();

    /**
     * We generate the random seed only once per store instance.
     * This is to ensure that the randomization is consistent across searches - for pagination purposes.
     */
    const randomSeed = ref<number>(getRandomSeed());

    const result = computed<C[]>(() => searchState.value?.contents ?? []);
    const isLoading = computed(() => searchState.value?.isLoading ?? false);
    const hasMore = computed(() => searchState.value?.cursor !== null);
    const pageTotal = computed(() => searchState.value?.pageTotal ?? 0);
    const search = (
      query: string,
      filter: Filter,
      limit: number,
      sort: Sort[] = [],
      groupFilterMode: GroupFilterMode = 'or',
      randomize: boolean = false,
    ) => {
      if (isLoading.value) {
        console.debug('Search is already loading, skipping.');
        return;
      }

      if (
        query === searchState.value?.query &&
        deepEqual(filter, searchState.value?.filter) &&
        deepEqual(sort, searchState.value?.sort)
      ) {
        console.debug('Search query is considered the same, skipping.');
        return;
      }

      /* Parameter "filter" is passed in as a reference,
    which causes the deepEqual to always be truthy, so solve this,
    we create a deep copy of it */
      const formatedFilter: Filter = JSON.parse(JSON.stringify(filter));

      searchState.value = {
        contentType,
        query,
        filter: formatedFilter,
        sort,
        cursor: null,
        pageSize: limit,
        pageTotal: 0,
        isLoading: true,
        contents: [],
        groupFilterMode,
        randomize,
      };

      const searchQuery: SearchQuery = {
        q: query,
        filter,
        pagination: { size: limit },
        sort,
        groupFilterMode,
      };

      if (randomize) {
        /**
         * Even if we send a random seed every time, the search API will NOT randomize the results
         * if the query is provided or if sort parameters are specified.
         */
        searchQuery.randomSeed = randomSeed.value;
      }

      searchContent<C>(contentType, searchQuery)
        .then(({ data, cursor, total }) => {
          searchState.value = {
            ...searchState.value!,
            cursor,
            pageTotal: total,
            isLoading: false,
            contents: data,
          };
        })
        .catch(() => {
          searchState.value = undefined;
        });
    };

    const next = () => {
      if (!searchState.value || isLoading.value || !hasMore.value) {
        return;
      }

      searchState.value = {
        ...searchState.value,
        isLoading: true,
      };

      const searchQuery: SearchQuery = {
        q: searchState.value.query,
        filter: searchState.value.filter,
        pagination: { size: searchState.value.pageSize!, cursor: searchState.value.cursor! },
        sort: searchState.value?.sort,
        groupFilterMode: searchState.value?.groupFilterMode,
      };

      if (searchState.value.randomize) {
        searchQuery.randomSeed = randomSeed.value;
      }
      searchContent<C>(searchState.value.contentType, searchQuery).then(({ data, cursor }) => {
        searchState.value = {
          ...searchState.value!,
          cursor,
          isLoading: false,
          contents: [...searchState.value!.contents, ...data],
        };
      });
    };

    const clear = () => {
      searchState.value = undefined;
    };

    return {
      result,
      isLoading,
      hasMore,
      total: pageTotal,
      search,
      next,
      clear,
    };
  })();

export default useSearch;
